import React, { useEffect, useMemo, useRef, useState } from 'react';

// Presentation Things
import { Dialog, DialogHandle } from '../../components/Dialog';
import { Divider, Fieldset } from '../../components/Fieldset';
import { TableChips } from '../../components/Table';
import { Button } from '../../components/Buttons';
import { Chips } from '../../components/Chips';
import { Input } from '../../components/Inputs';

// Data Things
import { isEmptyObject, onChangeInput, toDateString } from '../../constants/functions';
import { MoveOutEmployeesFilter } from './helper';

type FilterBodyProps = {
  onChange: React.Dispatch<React.SetStateAction<MoveOutEmployeesFilter>>;
  onSubmit(e: React.FormEvent): void;
  model: MoveOutEmployeesFilter;
};

type Props = {
  filter: MoveOutEmployeesFilter;
  open: boolean;
  onChangeFilter: (filter: MoveOutEmployeesFilter) => void;
  onSubmit?: (filter: MoveOutEmployeesFilter) => void;
  onClose: () => void;
};

export const Filter = ({ open, filter, onChangeFilter, onClose, onSubmit }: Props) => {
  /* States */
  const [tempModel, setTempModel] = useState(filter);

  /* Variables */
  const hasActiveFilter = useMemo(() => !isEmptyObject(filter), [filter]);

  /* Refs */
  const _dialog = useRef<DialogHandle>(null);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (onSubmit) {
      onChangeFilter(tempModel);
      onSubmit(tempModel);
      onClose();
    }
  };

  const handleClose = () => {
    setTempModel(filter);
    onClose();
  };

  const onDeselectMoveIn = () => {
    if (onSubmit) {
      onChangeFilter({ ...tempModel, moveInFrom: null, moveInTo: null });
      onSubmit({ ...tempModel, moveInFrom: null, moveInTo: null });
    }
  };

  const onDeselectMoveOut = () => {
    if (onSubmit) {
      onChangeFilter({ ...tempModel, moveOutFrom: null, moveOutTo: null });
      onSubmit({ ...tempModel, moveOutFrom: null, moveOutTo: null });
    }
  };

  useEffect(() => setTempModel(filter), [filter]);

  return (
    <>
      <Dialog
        open={open}
        ref={_dialog}
        onClose={handleClose}
        contents={[
          {
            title: 'Szűrő',
            body: <FilterBody model={tempModel} onChange={setTempModel} onSubmit={handleSubmit} />,
            footer: <Button title="Kész" type="submit" className="grow" form="moveOutFilter" />
          }
        ]}
      />

      <TableChips hasActiveFilter={hasActiveFilter}>
        {(filter.moveInFrom || filter.moveInTo) && (
          <Chips label="Beköltözve" from={filter.moveInFrom ? toDateString(filter.moveInFrom) : undefined} to={filter.moveInTo ? toDateString(filter.moveInTo) : undefined} onDelete={onDeselectMoveIn} />
        )}

        {(filter.moveOutFrom || filter.moveOutTo) && (
          <Chips label="Kiköltözve" from={filter.moveOutFrom ? toDateString(filter.moveOutFrom) : undefined} to={filter.moveOutTo ? toDateString(filter.moveOutTo) : undefined} onDelete={onDeselectMoveOut} />
        )}
      </TableChips>
    </>
  );
};

const FilterBody = ({ onChange, onSubmit, model }: FilterBodyProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => onChangeInput(e, onChange);

  return (
    <form className="p-5 flex flex-col gap-y-8 text-sm pb-8" onSubmit={onSubmit} id="moveOutFilter">
      <Fieldset label="Beköltözés" divided>
        <Input filter wrapperClassName="flex-1" type="date" label="Kezdés" value={model.moveInFrom} name="moveInFrom" onChange={handleChange} />
        <Divider />
        <Input filter wrapperClassName="flex-1" type="date" label="Befejezés" min={model.moveInFrom} value={model.moveInTo} name="moveInTo" onChange={handleChange} />
      </Fieldset>

      <Fieldset label="Kiköltözés" divided>
        <Input filter wrapperClassName="flex-1" type="date" label="Kezdés" value={model.moveOutFrom} name="moveOutFrom" onChange={handleChange} />
        <Divider />
        <Input filter wrapperClassName="flex-1" type="date" label="Befejezés" min={model.moveOutFrom} value={model.moveOutTo} name="moveOutTo" onChange={handleChange} />
      </Fieldset>
    </form>
  );
};
